import {Navigate, useNavigate} from "react-router-dom";
import { MdEdit } from "react-icons/md";
import styles from "../styles/editProfile.module.css";
import { useUser } from "@/entities/user/model/UserContext";
import { useState, useEffect } from "react";
import { updateProfileData } from "@/entities/user/api/userApi.js";
import {toast} from "react-toastify";
import Header from "@/widgets/header/Header";
import Footer from "@/widgets/footer/Footer";
import {compressImage} from "@/shared/lib/compressImage/compressImage.js";

/**
 * Страница редактирования профиля
 * ---------------------------------------------------------------------------
 * - Отображает текущие данные пользователя, если он авторизован
 * - Позволяет редактировать текстовые поля и загружать новое изображение
 * - Отправляет данные в формате multipart/form-data для корректной загрузки файла
 * - Отправляет форму через функцию `sendProfileData` (замени на свою реализацию)
 * ---------------------------------------------------------------------------
 */
const EditProfilePage = () => {
    const navigate = useNavigate();
    const { user, setUser, loading } = useUser();

    const [currentProfile, setCurrentProfile] = useState({
        name: "",
        surname: "",
        email: "",
        phone: "",
        dateOfBirth: "",
    });

    const [password, setPassword] = useState("");
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false); // ← новое состояние

    useEffect(() => {
        if (user && !loading) {
            setCurrentProfile({
                name: user.name ?? "",
                surname: user.surname ?? "",
                email: user.email ?? "",
                phone: user.phone ?? "",
                dateOfBirth: user.dateOfBirth ?? "",
            });
            setImagePreview(user.imageUrl ?? "/images/plug.avif");
        }
    }, [user, loading]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentProfile((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) {
            toast.error("Размер файла не должен превышать 10 МБ");
            return;
        }

        try {
            const compressedFile = await compressImage(file, 0.4);

            setImageFile(compressedFile);
            setImagePreview(URL.createObjectURL(compressedFile));
        } catch (error) {
            console.error("Ошибка при сжатии изображения:", error);
            toast.error("Ошибка при сжатии изображения");
        }
    };




    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const formData = new FormData();
        formData.append("name", currentProfile.name);
        formData.append("surname", currentProfile.surname);
        formData.append("email", currentProfile.email);
        formData.append("phone", currentProfile.phone);
        formData.append("dateOfBirth", currentProfile.dateOfBirth);
        if (password) formData.append("password", password);
        if (imageFile) formData.append("image", imageFile);

        try {
            const res = await sendProfileData(formData);
            setUser(res.data);
            toast.success("Профиль успешно обновлён");
            navigate("/profile");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Ошибка при обновлении профиля");
        } finally {
            setIsSubmitting(false);
        }
    };


    async function sendProfileData(formData) {
        return updateProfileData(formData);
    }

    if (loading) {
        return (
            <main>
                <Header />
                <section className={`container-custom ${styles.editProfileContainer} text-center`}>
                    <p className="par1">Загрузка профиля...</p>
                </section>
                <Footer />
            </main>
        );
    }

    if (!user) {
        return <Navigate to="/profile" replace />;
    }
    //             <main>
    //                 <Header />
    //                 <section className={`container-custom ${styles.editProfileContainer} text-center`}>
    //                     <p className="text-error par2">Ошибка: профиль не найден</p>
    //                 </section>
    //                 <Footer />
    //             </main>
    return (
        <main>
            <Header />
            <section className={`container-custom ${styles.editProfileContainer}`}>
                <div className="d-flex flex-column">
                    <button className="btn-word red gap-2" onClick={() => navigate(-1)}>
                        <img src="/images/arrow.svg" alt="" />
                        <p className="body capital">Назад</p>
                    </button>
                    <div className={`d-flex flex-column ${styles.imgWrapper}`}>
                        {imagePreview ? (
                            <img className={styles.profileImg} src={imagePreview} alt="Фото профиля" />
                        ) : (
                            <img className={styles.profileImg} src={"/images/plug.avif"} alt="Фото профиля" />
                        )}
                        <label className="btn-word mt-2 justify-content-center" htmlFor="avatarInput">
                            Изменить
                            <input
                                id="avatarInput"
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                hidden
                            />
                        </label>
                    </div>
                </div>
                <form onSubmit={handleSubmit} className={styles.formWrapper}>
                    <div className={styles.inputGroup}>
                        <input
                            type="text"
                            name="name"
                            className={styles.inputField}
                            value={currentProfile.name}
                            onChange={handleInputChange}
                            placeholder="Имя"
                        />
                        <MdEdit size={18} />
                    </div>
                    <div className={styles.inputGroup}>
                        <input
                            type="text"
                            name="surname"
                            className={styles.inputField}
                            value={currentProfile.surname}
                            onChange={handleInputChange}
                            placeholder="Фамилия"
                        />
                        <MdEdit size={18} />
                    </div>
                    <div className={styles.inputGroup}>
                        <input
                            type="email"
                            name="email"
                            className={styles.inputField}
                            value={currentProfile.email}
                            onChange={handleInputChange}
                            placeholder="email"
                        />
                        <MdEdit size={18} />
                    </div>
                    <div className={styles.inputGroup}>
                        <input
                            type="date"
                            name="dateOfBirth"
                            className={styles.inputField}
                            value={currentProfile.dateOfBirth}
                            disabled
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <input
                            type="tel"
                            name="phone"
                            className={styles.inputField}
                            value={currentProfile.phone}
                            onChange={handleInputChange}
                            placeholder="Номер телефона"
                        />
                        <MdEdit size={18} />
                    </div>
                    <div className={styles.inputGroup}>
                        <input
                            type="password"
                            name="password"
                            className={styles.inputField}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Пароль"
                        />
                        <MdEdit size={18} />
                    </div>
                    <button
                        type="submit"
                        className={`${styles.submitButton} btn btn-red-filled w-100`}
                        disabled={isSubmitting} // ← блокировка
                    >
                        {isSubmitting ? "Загрузка..." : "Сохранить изменения"} {/* ← замена текста */}
                    </button>
                </form>
            </section>
            <Footer />
        </main>
    );
};

export default EditProfilePage;
